"use client";

import { Callout, Flex, Text, VisuallyHidden } from "@radix-ui/themes";
import * as React from "react";
import { Dialog, Button, Select, TextField } from "./elements.js";
import { Label } from "./elements.js";
import { isErrorLike } from "./utils.js";
import { useInviteUser } from "./api/user.js";
import { InviteMemberInput, MemberRole, useRoles } from "../api/endpoint.js";
import { Translation } from "./i18n/translation.js";
import { useTranslation } from "./i18n/use-translation.js";

/**
 * Used to stub a fake value for the role select. It will be selected by default
 * before the role query resolves, or if the query results in an error. We do
 * this because we need to provide _any_ value to the select to avoid
 * controlled/uncontrolled bugs.
 */
const PLACEHOLDER_ROLE = "_rolePlaceholder";

interface InviteUserDialogProps {
  children?: React.ReactNode;
}

export function InviteUserDialog({ children }: InviteUserDialogProps) {
  const translate = useTranslation();
  const [open, setOpen] = React.useState(false);
  const dialogId = toId("invite-user", React.useId());
  const formId = toId(dialogId, "form");

  const inviteUser = useInviteUser();
  const rolesQuery = useRoles({
    query: { initialData: [] },
  });
  const roles = rolesQuery.data;
  const [selectedRole, setSelectedRole] = React.useState(
    () => getDefaultRole(roles)?.slug || PLACEHOLDER_ROLE,
  );
  React.useEffect(() => {
    // Update the selected role if it's not in the list (eg if the list was
    // previously empty and the query resolved)
    setSelectedRole((selectedRole) => {
      if (roles.find((role) => role.slug === selectedRole)) {
        // if current selected role is in the new list, don't change it
        return selectedRole;
      }
      return getDefaultRole(roles)?.slug || PLACEHOLDER_ROLE;
    });
  }, [roles]);

  const onSubmitForm = (data: InviteMemberInput) => {
    if (inviteUser.isPending || rolesQuery.status !== "success") {
      return;
    }
    inviteUser.mutate(
      { data },
      {
        onSuccess: () => {
          setOpen(false);
        },
      },
    );
  };

  const formErrors = getFormErrors(inviteUser.error);
  useFormFieldFocusOnError(dialogId, inviteUser.error);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      {children && <Dialog.Trigger>{children}</Dialog.Trigger>}
      <Dialog.Content maxWidth="480px" key={String(open)}>
        <Dialog.Title>
          <Translation
            defaultMessage="Invite user"
            id="6+aYFi"
            description="Dialog title for inviting a user"
          />
        </Dialog.Title>
        <Dialog.Description>
          <Translation
            defaultMessage="An invitation will be sent to this email address with a link to complete their account."
            id="IicELr"
            description="Dialog description explaining invitation process"
          />
        </Dialog.Description>
        <Flex direction="column" gap="4" mt="5" asChild>
          <form
            id={formId}
            onSubmit={async (event) => {
              event.preventDefault();
              onSubmitForm({
                email: event.currentTarget.email.value,
                roles: [selectedRole],
              });
            }}
          >
            <FormField
              rootId={dialogId}
              name="email"
              label={translate({
                defaultMessage: "Email address",
                id: "ACNqhk",
                description: "Label for email address field",
              })}
              error={formErrors.fields.email}
              required
              control={(props) => (
                <TextField
                  {...props}
                  data-1p-ignore="true"
                  data-lpignore="true"
                  type="email"
                  autoComplete="off"
                  placeholder={translate({
                    defaultMessage: "Enter an email address",
                    id: "0ulARV",
                    description: "Placeholder for email address field",
                  })}
                />
              )}
            />

            <FormField
              rootId={dialogId}
              name="role"
              label={translate({
                defaultMessage: "Role",
                id: "1Htc3a",
                description: "Label for role selection field",
              })}
              error={formErrors.fields.role}
              disabled={rolesQuery.isPending || roles.length <= 1}
              info={
                roles.length === 1 ? (
                  <Translation
                    defaultMessage="New users will be invited with the {roleName} role, as it is the only one available."
                    id="/CNKkm"
                    description="Info message when only one role is available"
                    values={{
                      roleName: <Text weight="bold">{roles[0].name}</Text>,
                    }}
                  />
                ) : undefined
              }
              control={({
                id,
                "aria-invalid": ariaInvalid,
                "aria-describedby": ariaDescribedBy,
                ...props
              }) => (
                <Select.Root
                  {...props}
                  value={selectedRole}
                  onValueChange={setSelectedRole}
                >
                  <Select.Trigger
                    id={id}
                    aria-invalid={ariaInvalid}
                    aria-describedby={ariaDescribedBy}
                  />
                  <Select.Content>
                    <Select.Item value={PLACEHOLDER_ROLE} disabled>
                      <Translation
                        defaultMessage="Select a role"
                        id="0VMIWs"
                        description="Placeholder option for role selection"
                      />
                    </Select.Item>
                    {roles.map((role) => (
                      <Select.Item key={role.slug} value={role.slug}>
                        {role.name}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              )}
            />
          </form>
        </Flex>

        {formErrors.form ? (
          <Callout.Root color="red" mt="4" mb="-2">
            <Callout.Text>{formErrors.form}</Callout.Text>
          </Callout.Root>
        ) : null}

        <Flex mt="5" gap="3" justify="end">
          <Dialog.Close>
            <Button variant="secondary" disabled={inviteUser.isPending}>
              <Translation
                defaultMessage="Cancel"
                id="hHNj31"
                description="Cancel button text"
              />
            </Button>
          </Dialog.Close>
          <Button
            type="submit"
            form={formId}
            loading={inviteUser.isPending}
            disabled={rolesQuery.isPending || undefined}
          >
            <Translation
              defaultMessage="Invite"
              id="JIQROV"
              description="Invite button text to send invitation"
            />
          </Button>
        </Flex>
        {/* mirror errors in a live region */}
        <VisuallyHidden asChild>
          <section aria-live="polite">{formErrors.form}</section>
        </VisuallyHidden>
      </Dialog.Content>
    </Dialog.Root>
  );
}

interface FormControlRenderProps {
  id: string;
  name: string;
  "aria-describedby": string | undefined;
  "aria-invalid"?: boolean;
  required: boolean | undefined;
  disabled: boolean | undefined;
}

function FormField({
  rootId,
  name,
  label,
  error,
  info,
  control,
  required,
  disabled,
}: {
  rootId: string;
  name: string;
  label: string;
  error?: React.ReactNode;
  info?: React.ReactNode;
  control: (props: FormControlRenderProps) => React.ReactNode;
  required?: boolean;
  disabled?: boolean;
}) {
  const fieldId = toId(rootId, name);
  const errorId = toId(rootId, name, "error");
  const infoId = toId(rootId, name, "info");
  return (
    <Flex direction="column" gap="1">
      <Label htmlFor={fieldId}>{label}</Label>
      {control({
        id: fieldId,
        name,
        "aria-describedby": (() => {
          const tags: string[] = [];
          if (error) {
            tags.push(errorId);
          }
          if (info) {
            tags.push(infoId);
          }
          if (tags.length === 0) {
            return undefined;
          }
          return tags.join(" ");
        })(),
        "aria-invalid": !!error || undefined,
        required: required || undefined,
        disabled: disabled || undefined,
      })}

      {error ? (
        <Text color="red" size="2" id={errorId}>
          {error}
        </Text>
      ) : null}
      {info ? (
        <Text color="gray" size="2" id={infoId} mt="1">
          {info}
        </Text>
      ) : null}
    </Flex>
  );
}

function toId(...parts: string[]) {
  return parts.join("-");
}

function getFormErrors(queryError: unknown) {
  const formErrors = {
    form: null as string | null,
    fields: {
      email: null as string | null,
      role: null as string | null,
    },
  };

  if (queryError) {
    if (!isErrorLike(queryError)) {
      return {
        ...formErrors,
        form: "An unexpected error occurred. Please try again.",
      };
    }

    switch (queryError.message.toLowerCase()) {
      case "user already exists":
      case "user already invited":
      case "invalid email":
        formErrors.fields.email = queryError.message;
        break;
      case "invalid role":
        formErrors.fields.role = queryError.message;
        break;
      default:
        // TODO handle more cases for various server errors
        formErrors.form =
          "There was an error inviting this user. Please refresh the page and try again.";
        break;
    }
  }

  return formErrors;
}

// Note: Error messages in getFormErrors are kept as plain strings
// since they are displayed dynamically

function useFormFieldFocusOnError(dialogId: string, queryError: unknown) {
  React.useEffect(() => {
    const fieldErrors = getFormErrors(queryError).fields;
    for (const [name, error] of Object.entries(fieldErrors)) {
      if (error) {
        const fieldElement = document.getElementById(toId(dialogId, name)) as
          | HTMLInputElement
          | HTMLButtonElement
          | null;
        if (fieldElement) {
          fieldElement?.focus();
          if ("select" in fieldElement) {
            fieldElement.select();
          }
        }
        break;
      }
    }
  }, [dialogId, queryError]);
}

function getDefaultRole(roles: MemberRole[]) {
  return roles.find((role) => role.default) || roles[0];
}
